import type { ActorRefFrom, AnyActorLogic } from 'xstate';
import z from 'zod';
import { optionSchema } from '~/features/types';
import { isStartDateAllowed, START_DATE_IN_PAST_MESSAGE } from '../../../shared-steps/startDate';

export interface Context {
  loading: boolean;
  parentRef?: ActorRefFrom<AnyActorLogic>;
}

export type OnAddEvent = {
  type: 'ON_ADD';
  payload: { values: LaborMetricsFormValues };
};

export type OnUpdateEvent = {
  type: 'ON_UPDATE';
  payload: { values: LaborMetricsFormValues };
};

export type Events = OnAddEvent | OnUpdateEvent;

// [MCV-1524] A factory rather than a const: the past-date rule needs to know
// what start date is already on the record, so an existing listing whose
// start date has passed can still be edited. See ./shared-steps/startDate.
export const createLaborMetricsFormSchema = (persistedStartDate?: string) =>
  z
    .object({
      id: z.number().optional(),
      jobListingId: z.number().optional(),
      startDate: z.iso.date('Invalid date'),
      endDate: z.string().optional(),
      laborReqType: optionSchema,
      numOfShifts: z.preprocess(
        (arg) => (arg == null ? null : Number(arg)),
        z.number().min(1, 'Required')
      ),
      hoursPerShift: z.preprocess(
        (arg) => (arg == null ? null : Number(arg)),
        z.number().min(1, 'Required')
      ),
      minRvusPerShift: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        z.number().optional().nullable()
      ),
      numRvusReq: z.preprocess(
        (arg) => (arg === '' || arg == null ? null : Number(arg)),
        z.number().optional().nullable()
      ),
    })
    .refine(
      (schema) => {
        if (schema.laborReqType.value === 'RVU') {
          return schema.minRvusPerShift !== undefined && schema.minRvusPerShift !== null;
        }
        return true;
      },
      {
        message: 'Required',
        path: ['minRvusPerShift'],
      }
    )
    .refine(
      (schema) => {
        if (schema.laborReqType.value === 'RVU') {
          return schema.numRvusReq !== undefined && schema.numRvusReq !== null;
        }
        return true;
      },
      {
        message: 'Required',
        path: ['numRvusReq'],
      }
    )
    .refine((schema) => isStartDateAllowed(schema.startDate, persistedStartDate), {
      message: START_DATE_IN_PAST_MESSAGE,
      path: ['startDate'],
    });

export type Scope = {
  context: Context;
  event: Events;
};

export type LaborMetricsFormValues = z.infer<ReturnType<typeof createLaborMetricsFormSchema>>;
